import { getEmojiColorPalette } from "@/components/emoji/emoji-colors.ts";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useEmployeeSurveySaveDraft } from "@/queries/employees/use-employee-survey.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiSurveyRespond, ApiSurveyType } from "@/types/survey.types.ts";
import { Avatar, Button, FullScreenPage, ListItem, Stack, Title } from "@skailer-com/uikit";
import { round } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const SurveyStart = ({ survey }: { survey: ApiSurveyRespond }) => {
  const { t } = useTranslation();

  const startSurvey = useEmployeeSurveySaveDraft();
  const { data: employee } = useEmployeeMe();
  const navigate = useNavigate();
  const { renderDate } = useFormatter();

  const handleStartSurvey = async () => {
    if (!employee) return;
    try {
      await startSurvey.mutateAsync({
        employeeId: employee.id,
        questionnaireId: survey.id,
        answers: [],
      });

      analyticsService.trackEvent(analyticEvents.surveys.responseStarted, {
        [analyticProperties.id]: survey.id,
      });
    } catch {
      // Handle error
    }
  };

  const isRequest = survey.type === ApiSurveyType.survey_with_approval;

  const emojiColors = useMemo(
    () => (survey.emoji ? getEmojiColorPalette(survey.emoji) : undefined),
    [survey]
  );

  return (
    <>
      <FullScreenPage
        backgroundSlot={
          emojiColors ? (
            <GradientContainer colorPalette={emojiColors} addLightOverlayTop={true} />
          ) : undefined
        }
        titleSlot={<Title header={survey.title} />}
        isCenterVertical={false}
        backSlot={
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigate("/");
            }}
          >
            {t("common:exit")}
          </Button>
        }
      >
        <Stack className={"card-panel p-6 bg-light"} justify={"between"} gap={"xl"}>
          <Avatar emoji={survey.emoji} size={"2xl"} variant={"opaque"} className={"shadow-lg"} />
          <Title header={survey.title} caption={survey.description || undefined} size={"lg"} />
          <Stack>
            {survey.target_employee && (
              <ListItem
                title={t("core:employee")}
                valueSlot={<EmployeeItem employee={survey.target_employee} enablePreview />}
              />
            )}
            <ListItem
              title={t("common:questions")}
              value={survey.form_stats.total_questions.toString()}
            />
            {!isRequest && (
              <ListItem
                title={t("common:type")}
                value={survey.is_anonymous ? t("common:anonymous") : t("common:non_anonymous")}
              />
            )}
            {!isRequest && (
              <ListItem
                title={t("common:time_to_complete")}
                value={t("plurals:plural_minutes", {
                  count: round(survey.form_stats.total_estimated_time_seconds / 60),
                })}
              />
            )}
            {survey.date_starts_at && survey.date_ends_at && (
              <ListItem
                title={t("common:dates")}
                value={`${renderDate(survey.date_starts_at)} - ${renderDate(survey.date_ends_at)}`}
              />
            )}
          </Stack>
          <Stack>
            <Button onClick={handleStartSurvey} isLoading={startSurvey.isPending} size={"lg"}>
              {isRequest ? t("surveys:request_fill") : t("surveys:start_survey")}
            </Button>
          </Stack>
        </Stack>
      </FullScreenPage>
    </>
  );
};
